import React, { useEffect, useMemo, useState } from "react";
import { api, apiErr } from "@/lib/api";
import PortalLayout from "@/components/PortalLayout";
import { Button } from "@/components/ui/Button";
import { toast } from "sonner";
import {
  Loader2, X, ChevronRight, ChevronDown, Search, KeyRound, Lock, CheckCircle2, XCircle,
  Circle, Clock, Send, Building2,
} from "lucide-react";

const STATUS_META = {
  draft:            { label: "Ciornă",            fg: "#6b7280", bg: "#f4f4f5" },
  submitted:        { label: "În analiză Manager", fg: "#a16207", bg: "#fef9c3" },
  manager_approved: { label: "În analiză PM",     fg: "#1d4ed8", bg: "#dbeafe" },
  approved:         { label: "Aprobată",          fg: "#166534", bg: "#dcfce7" },
  rejected:         { label: "Respinsă",          fg: "#b91c1c", bg: "#fee2e2" },
  expired:          { label: "Expirată",          fg: "#6b7280", bg: "#f4f4f5" },
  revoked:          { label: "Revocată",          fg: "#6b7280", bg: "#f4f4f5" },
};

const ACCESS_STATUS_META = {
  active:    { label: "Acces activ",     fg: "#166534", bg: "#dcfce7" },
  requested: { label: "Cerere trimisă", fg: "#a16207", bg: "#fef9c3" },
  no_access: { label: "Fără acces",      fg: "#6b7280", bg: "#f4f4f5" },
  revoked:   { label: "Acces revocat",   fg: "#b91c1c", bg: "#fee2e2" },
};

const ACCESS_ACTION_LABEL = {
  create:           "Cerere creată",
  submit:           "Trimisă spre aprobare",
  manager_approve:  "Aprobată de Manager Superior",
  manager_reject:   "Respinsă de Manager Superior",
  pm_approve:       "Aprobată de Project Manager",
  pm_reject:        "Respinsă de Project Manager",
  request_info:     "Cerere de informații suplimentare",
  provide_info:     "Informații suplimentare furnizate",
  withdraw:         "Cerere retrasă",
  revoke:           "Acces revocat",
  expire:           "Acces expirat",
};

function StatusPill({ status, map = STATUS_META }) {
  const m = map[status] || { label: status, fg: "#374151", bg: "#f4f4f5" };
  return (
    <span className="inline-flex items-center px-2.5 h-6 text-[11px] font-semibold rounded-full"
      style={{ background: m.bg, color: m.fg }}>{m.label}</span>
  );
}

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso.length === 10 ? iso + "T00:00:00" : iso);
  if (isNaN(d)) return "—";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
}


export default function AccessRequests() {
  const [tab, setTab] = useState("catalog"); // catalog | my
  const [catalog, setCatalog] = useState([]);
  const [myRequests, setMyRequests] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [requestFor, setRequestFor] = useState(null); // project object
  const [detail, setDetail] = useState(null); // request obj
  const [q, setQ] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [c, m, r] = await Promise.all([
        api.get("/access-requests/projects-catalog"),
        api.get("/access-requests/mine"),
        api.get("/access-requests/role-options"),
      ]);
      setCatalog(c.data);
      setMyRequests(m.data);
      setRoles(r.data.roles);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  const filteredCatalog = useMemo(() => {
    if (!q.trim()) return catalog;
    const s = q.toLowerCase();
    return catalog.filter(p =>
      (p.code || "").toLowerCase().includes(s) ||
      (p.name || "").toLowerCase().includes(s) ||
      (p.location || "").toLowerCase().includes(s));
  }, [catalog, q]);

  return (
    <PortalLayout variant="staff">
      <div className="max-w-[1200px] mx-auto">
        <header className="pb-10 pt-2">
          <div className="text-[12px] font-display font-medium text-ink-muted mb-3">
            Acces la proiecte
          </div>
          <h1 className="text-[38px] md:text-[46px] leading-[1.05] font-display font-semibold tracking-[-0.02em] text-ink">
            Alocare & permisiuni
          </h1>
          <p className="mt-4 text-[15px] leading-relaxed text-ink-muted max-w-xl">
            Vezi ce proiecte îți sunt disponibile și cere acces suplimentar când ai nevoie.
          </p>
        </header>

        <div className="pio-tabs mb-6">
          {[
            { k: "catalog", l: "Proiecte disponibile", count: catalog.length },
            { k: "my", l: "Cererile mele", count: myRequests.length },
          ].map(t => (
            <button key={t.k} onClick={() => setTab(t.k)}
              className={`pio-tab !text-[14px] ${tab === t.k ? "is-active" : ""}`}>
              {t.l}
              <span className="text-[11px] font-mono tabular-nums opacity-60 ml-1.5">({t.count})</span>
            </button>
          ))}
        </div>

        {tab === "catalog" && (
          <>
            {/* Dropdown pentru proiectele fără acces */}
            <RequestAccessDropdown
              projects={catalog.filter(p => p.access_status === "no_access" || p.access_status === "revoked")}
              onSelect={(p) => setRequestFor(p)}
            />

            <div className="pb-4 mb-2 flex items-center gap-4">
              <div className="flex items-center gap-2.5 flex-1 max-w-[420px] h-10 px-4 rounded-full border border-slate-200 bg-white/70 backdrop-blur focus-within:border-indigo-400 transition-colors">
                <Search className="h-4 w-4 text-slate-400" />
                <input value={q} onChange={e => setQ(e.target.value)}
                  placeholder="Caută în proiectele active"
                  style={{ boxShadow: "none" }}
                  className="flex-1 bg-transparent outline-none border-0 text-[14px] placeholder:text-slate-400" />
              </div>
              <div className="text-[13px] text-slate-500 font-mono tabular-nums ml-auto whitespace-nowrap">
                {filteredCatalog.filter(p => p.access_status !== "no_access" && p.access_status !== "revoked").length} active
              </div>
            </div>

            <div className="surface overflow-hidden">
              {loading ? (
                <div className="py-24 flex items-center justify-center">
                  <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
                </div>
              ) : (
                <div className="divide-y divide-indigo-50">
                  {filteredCatalog
                    .filter(p => p.access_status !== "no_access" && p.access_status !== "revoked")
                    .map(p => (
                      <ProjectRow key={p.id} project={p} />
                    ))}
                </div>
              )}
              {!loading && filteredCatalog.filter(p => p.access_status !== "no_access" && p.access_status !== "revoked").length === 0 && (
                <div className="py-16 text-center text-ink-muted text-[14px]">
                  Nu ai proiecte active momentan.
                </div>
              )}
            </div>
          </>
        )}

        {tab === "my" && (
          <div className="surface overflow-hidden">
            {loading ? (
              <div className="py-24 flex items-center justify-center">
                <Loader2 className="h-5 w-5 animate-spin text-ink-muted" />
              </div>
            ) : myRequests.length === 0 ? (
              <div className="py-24 text-center text-ink-muted">
                <KeyRound className="h-8 w-8 mx-auto text-neutral-300 mb-3" />
                <div className="text-[15px]">Nu ai trimis cereri de acces încă.</div>
              </div>
            ) : (
              <div className="divide-y divide-indigo-50">
                {myRequests.map(r => (
                  <RequestRow key={r.id} request={r} onClick={() => setDetail(r)} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <RequestForm open={!!requestFor} project={requestFor}
        roles={roles} onClose={() => setRequestFor(null)}
        onSaved={async () => { setRequestFor(null); await load(); setTab("my"); }} />

      <RequestDetail open={!!detail} request={detail}
        onClose={() => setDetail(null)}
        onChanged={async () => { setDetail(null); await load(); }} />
    </PortalLayout>
  );
}


function ProjectRow({ project }) {
  return (
    <div className="w-full flex items-center gap-4 px-5 py-5 hover:bg-indigo-50/50 transition-colors">
      <div className="h-11 w-11 rounded-full flex items-center justify-center bg-indigo-50 text-indigo-500 shrink-0">
        <Building2 className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2.5 mb-1">
          <div className="text-[15px] font-medium text-ink truncate">{project.code} — {project.name}</div>
          <StatusPill status={project.access_status} map={ACCESS_STATUS_META} />
        </div>
        <div className="text-[13px] text-ink-muted truncate">
          {project.location || "—"}
          {project.project_manager && ` · PM: ${project.project_manager.name}`}
          {project.current_role && ` · Rolul tău: ${project.current_role}`}
        </div>
      </div>
      {project.access_status === "requested" && (
        <div className="text-[12px] text-amber-700">Așteaptă aprobare</div>
      )}
    </div>
  );
}


function RequestAccessDropdown({ projects, onSelect }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = React.useRef(null);

  useEffect(() => {
    function onClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const filtered = q.trim()
    ? projects.filter(p => (p.code + " " + p.name + " " + (p.location || "")).toLowerCase().includes(q.toLowerCase()))
    : projects;

  return (
    <div className="mb-6" ref={ref}>
      <div className="text-[12px] font-display font-medium text-ink-muted mb-2">
        Solicită acces pe proiect
      </div>
      <div className="relative">
        <button
          onClick={() => setOpen(v => !v)}
          disabled={projects.length === 0}
          className="w-full max-w-[520px] h-12 px-4 border border-slate-200 bg-white/70 backdrop-blur hover:border-indigo-400 hover:bg-indigo-50/40 text-left rounded-xl flex items-center justify-between transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          <span className="flex items-center gap-2.5">
            <span className="h-7 w-7 rounded-full bg-gradient-to-br from-[#3b8ff0] to-[#9b6bf0] text-white flex items-center justify-center shrink-0">
              <KeyRound className="h-3.5 w-3.5" />
            </span>
            <span className="text-[14px] text-ink">
              {projects.length === 0
                ? "Ai deja acces la toate proiectele"
                : `Selectează un proiect (${projects.length} disponibile)`}
            </span>
          </span>
          <ChevronDown className={`h-4 w-4 text-ink-muted transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && projects.length > 0 && (
          <div className="absolute top-full left-0 right-0 max-w-[520px] mt-1.5 bg-white border border-slate-200 rounded-xl shadow-[0_20px_50px_-15px_rgba(50,70,180,0.3)] z-10 max-h-[400px] overflow-hidden flex flex-col">
            <div className="px-3 py-2 border-b border-slate-100">
              <input
                autoFocus value={q} onChange={e => setQ(e.target.value)}
                placeholder="Caută în listă..."
                style={{ boxShadow: "none" }}
                className="w-full text-[13px] outline-none bg-transparent border-0"
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 ? (
                <div className="py-6 text-center text-[13px] text-ink-muted">Fără rezultate</div>
              ) : (
                filtered.map(p => (
                  <button
                    key={p.id}
                    onClick={() => { setOpen(false); setQ(""); onSelect(p); }}
                    className="w-full text-left px-4 py-3 hover:bg-indigo-50/50 border-b border-slate-100 last:border-b-0 transition-colors">
                    <div className="flex items-center gap-2 text-[14px] font-medium text-ink truncate">
                      {p.code} — {p.name}
                      {p.access_status === "revoked" && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-mono uppercase tracking-wider">Revocat</span>
                      )}
                    </div>
                    <div className="text-[12px] text-ink-muted mt-0.5 truncate">
                      {p.location || "—"}
                      {p.project_manager && ` · PM: ${p.project_manager.name}`}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


function RequestRow({ request, onClick }) {
  return (
    <button onClick={onClick}
      className="group w-full flex items-center gap-4 px-5 py-4 hover:bg-indigo-50/50 transition-colors text-left">
      <div className="h-11 w-11 rounded-full flex items-center justify-center bg-indigo-50 text-indigo-500 shrink-0">
        <KeyRound className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <div className="text-[15px] font-medium text-ink truncate">
            {request.project?.code || "—"}
          </div>
          <StatusPill status={request.status} />
        </div>
        <div className="text-[13px] text-ink-muted truncate">
          {request.project?.name} · Trimis {fmtDate(request.submitted_at || request.created_at)}
        </div>
      </div>
      <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 group-hover:translate-x-0.5 transition-all" />
    </button>
  );
}


function RequestForm({ open, onClose, project, roles, onSaved }) {
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open && project) {
      setForm({
        project_id: project.id,
        requested_role: "Membru proiect",
        reason: "", needed_from: "", needed_until: "",
        activities: [], notes: "",
      });
    }
  }, [open, project]);

  async function save(submitNow) {
    if (!form.reason?.trim()) { toast.error("Motivul cererii e obligatoriu"); return; }
    setBusy(true);
    try {
      await api.post("/access-requests", { ...form, submit_now: submitNow });
      toast.success(submitNow ? "Cerere trimisă" : "Ciornă salvată");
      onSaved();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  useEffect(() => { document.body.style.overflow = open ? "hidden" : ""; return () => { document.body.style.overflow = ""; }; }, [open]);

  if (!open || !project) return null;
  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-ink/20 z-40" />
      <aside className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[600px] bg-white border-l border-neutral-200 flex flex-col">
        <div className="px-8 pt-8 pb-6 border-b border-neutral-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2">
              Cerere de acces
            </div>
            <div className="text-[22px] font-display font-semibold text-ink truncate">
              {project.code} — {project.name}
            </div>
            <div className="text-[13px] text-ink-muted mt-1">
              {project.project_manager?.name && `PM: ${project.project_manager.name}`}
            </div>
          </div>
          <button onClick={onClose}
            className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-6">
          <F label="Motivul solicitării *">
            <textarea rows={3} value={form.reason || ""}
              onChange={e => setForm({ ...form, reason: e.target.value })}
              placeholder="Ex: Preiau activitățile de MEP pe zona de HVAC"
              className="w-full border border-neutral-200 rounded-lg p-3 text-[14px] outline-none focus:border-ink resize-none" />
          </F>
          <div className="grid grid-cols-2 gap-4">
            <F label="De la data">
              <input type="date" value={form.needed_from || ""}
                onChange={e => setForm({ ...form, needed_from: e.target.value })}
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
            </F>
            <F label="Până la data (opțional)">
              <input type="date" value={form.needed_until || ""}
                onChange={e => setForm({ ...form, needed_until: e.target.value })}
                className="w-full border-b border-neutral-200 py-2 outline-none focus:border-ink bg-transparent" style={{ borderRadius: 0 }} />
            </F>
          </div>
          <F label="Observații suplimentare">
            <textarea rows={2} value={form.notes || ""}
              onChange={e => setForm({ ...form, notes: e.target.value })}
              className="w-full border border-neutral-200 rounded-lg p-3 text-[14px] outline-none focus:border-ink resize-none" />
          </F>

          <div className="p-4 rounded-xl border border-indigo-100 bg-indigo-50/60 text-[13px] text-slate-600">
            Fluxul: <b>Manager Superior</b> (sau Admin) → Acces activat automat
          </div>
        </div>

        <div className="px-8 py-5 border-t border-neutral-100 flex items-center justify-end gap-3">
          <Button variant="ghost" size="md" onClick={onClose}>Renunță</Button>
          <Button variant="secondary" size="md" loading={busy} onClick={() => save(false)}>
            Salvează ciornă
          </Button>
          <Button variant="primary" size="md" icon={Send} loading={busy}
            onClick={() => save(true)} disabled={!form.reason?.trim()}>
            Trimite cererea
          </Button>
        </div>
      </aside>
    </>
  );
}


function RequestDetail({ open, request, onClose, onChanged }) {
  const [busy, setBusy] = useState(false);
  if (!open || !request) return null;
  const canDelete = request.status === "draft";

  async function del() {
    if (!window.confirm("Ștergi ciorna?")) return;
    setBusy(true);
    try {
      await api.delete(`/access-requests/${request.id}`);
      toast.success("Șters");
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  async function submit() {
    setBusy(true);
    try {
      await api.post(`/access-requests/${request.id}/submit`);
      toast.success("Cerere trimisă");
      onChanged();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div onClick={onClose} className="fixed inset-0 bg-ink/20 z-40" />
      <aside className="fixed top-0 right-0 bottom-0 z-50 w-full max-w-[600px] bg-white border-l border-neutral-200 flex flex-col">
        <div className="px-8 pt-8 pb-6 border-b border-neutral-100 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-[12px] font-display font-medium text-ink-muted mb-2 flex items-center gap-3">
              Cerere acces <StatusPill status={request.status} />
            </div>
            <div className="text-[22px] font-display font-semibold text-ink truncate">
              {request.project?.code} — {request.project?.name}
            </div>
          </div>
          <button onClick={onClose}
            className="h-10 w-10 flex items-center justify-center text-ink-muted hover:text-ink hover:bg-neutral-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8">
          <section>
            <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Motiv</div>
            <div className="text-[14px] text-ink">{request.reason}</div>
          </section>
          {(request.needed_from || request.needed_until) && (
            <section>
              <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Perioadă</div>
              <div className="text-[14px] text-ink">
                {fmtDate(request.needed_from)} → {request.needed_until ? fmtDate(request.needed_until) : "nedefinit"}
              </div>
            </section>
          )}
          <section>
            <div className="text-[12px] font-display font-medium text-ink-muted mb-3">Istoric</div>
            <div className="space-y-3">
              {(request.history || []).map((h, i) => (
                <div key={i} className="flex gap-4">
                  <div className="text-[11px] text-ink-muted tabular-nums min-w-[120px]">
                    {`${fmtDate(h.at)}, ${new Date(h.at).toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })}`}
                  </div>
                  <div>
                    <div className="text-[13px] text-ink"><span className="font-medium">{h.by_name}</span> · {ACCESS_ACTION_LABEL[h.action] || h.action}</div>
                    {h.comment && <div className="text-[12px] text-ink-muted">{h.comment}</div>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="px-8 py-5 border-t border-neutral-100 flex items-center gap-3">
          {canDelete && <Button variant="danger" size="md" onClick={del} loading={busy}>Șterge ciornă</Button>}
          <div className="ml-auto flex gap-3">
            <Button variant="ghost" size="md" onClick={onClose}>Închide</Button>
            {canDelete && (
              <Button variant="primary" size="md" icon={Send} onClick={submit} loading={busy}>
                Trimite cererea
              </Button>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}


function F({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[12px] font-medium text-ink-muted mb-2">{label}</span>
      {children}
    </label>
  );
}
